// Export utilities for Evaluations Recap — Excel (XLSX) and PDF
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';

// ── Color palette ──
const C = {
  purple:      'FF6D28D9',
  purpleMid:   'FF7C3AED',
  purpleBg:    'FFF5F3FF',
  success:     'FF10B981',
  successBg:   'FFECFDF3',
  danger:      'FFEF4444',
  dangerBg:    'FFFEF2F2',
  blue:        'FF3B82F6',
  blueBg:      'FFDBEAFE',
  amber:       'FFD97706',
  amberBg:     'FFFFFBEB',
  orange:      'FFEA580C',
  gray800:     'FF1E293B',
  gray600:     'FF475569',
  gray500:     'FF64748B',
  gray400:     'FF94A3B8',
  gray200:     'FFE2E8F0',
  gray100:     'FFF1F5F9',
  gray50:      'FFF8FAFC',
  white:       'FFFFFFFF',
  gold:        'FFF59E0B',
};

const noteColor = (n) => {
  if (n >= 16) return 'FF059669';
  if (n >= 14) return C.blue;
  if (n >= 10) return C.amber;
  return C.danger;
};

const noteColorRGB = (n) => {
  if (n >= 16) return [5, 150, 105];
  if (n >= 14) return [59, 130, 246];
  if (n >= 10) return [217, 119, 6];
  return [239, 68, 68];
};

// Map assessment type to label
const typeLabels = {
  tp: 'TP', exam: 'Examen', continuous: 'CC', project: 'Projet',
  quiz: 'Quiz', assignment: 'Devoir', lab: 'Lab', other: 'Autre',
};

function buildRecapStats(students, assessments, filterCourse) {
  const avgs = students.filter(s => {
    const avg = filterCourse !== 'all' ? s.filteredAverage : s.average;
    return avg !== null && avg !== undefined;
  }).map(s => filterCourse !== 'all' ? s.filteredAverage : s.average);

  return {
    totalStudents: students.length,
    totalAssessments: assessments.length,
    avg: avgs.length ? (avgs.reduce((a, b) => a + b, 0) / avgs.length).toFixed(2) : '0',
    best: avgs.length ? Math.max(...avgs).toFixed(1) : '0',
    worst: avgs.length ? Math.min(...avgs).toFixed(1) : '0',
    above10: avgs.filter(a => a >= 10).length,
    below10: avgs.filter(a => a < 10).length,
    successRate: avgs.length ? ((avgs.filter(a => a >= 10).length / avgs.length) * 100).toFixed(1) : '0',
  };
}

// ─────────────────────────────────────────────
//  EXCEL EXPORT
// ─────────────────────────────────────────────
export async function exportRecapToExcel(students, assessments, workspaceTitle, filterCourse) {
  const stats = buildRecapStats(students, assessments, filterCourse);
  const wb = new ExcelJS.Workbook();
  wb.creator = 'DevOpsLearn';
  wb.created = new Date();

  const ws = wb.addWorksheet('Récapitulatif', {
    properties: { defaultRowHeight: 20 },
    views: [{ showGridLines: false }],
  });

  const noBorder = { style: 'none' };
  const thinBorder = (color = C.gray200) => ({ style: 'thin', color: { argb: color } });

  // Dynamic columns: # | Candidat | Email | [assessment1] | [assessment2] | ... | Moyenne
  const totalCols = 3 + assessments.length + 1; // #, candidat, email, ...assessments, moyenne

  // ── Column widths ──
  const colDefs = [
    { key: 'rank', width: 6 },
    { key: 'name', width: 28 },
    { key: 'email', width: 26 },
  ];
  assessments.forEach((a, i) => {
    colDefs.push({ key: `a${i}`, width: 14 });
  });
  colDefs.push({ key: 'moyenne', width: 12 });
  ws.columns = colDefs;

  // ───────── ROW 1: Title banner ─────────
  const r1 = ws.addRow([]);
  ws.mergeCells(1, 1, 1, totalCols);
  const titleCell = ws.getCell(1, 1);
  titleCell.value = `Récapitulatif des notes — ${workspaceTitle}`;
  titleCell.font = { bold: true, size: 16, color: { argb: C.white }, name: 'Calibri' };
  titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purple } };
  titleCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  r1.height = 38;
  for (let c = 1; c <= totalCols; c++) {
    ws.getCell(1, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purple } };
    ws.getCell(1, c).border = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  }

  // ───────── ROW 2: Date + info ─────────
  const dateStr = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  const r2 = ws.addRow([]);
  ws.mergeCells(2, 1, 2, totalCols);
  const dateCell = ws.getCell(2, 1);
  dateCell.value = `Exporté le ${dateStr}  •  ${stats.totalStudents} candidat(s)  •  ${stats.totalAssessments} épreuve(s)  •  Scores normalisés sur /20`;
  dateCell.font = { size: 9, color: { argb: C.white }, name: 'Calibri', italic: true };
  dateCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleMid } };
  dateCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  r2.height = 22;
  for (let c = 1; c <= totalCols; c++) {
    ws.getCell(2, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleMid } };
    ws.getCell(2, c).border = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  }

  // ───────── ROW 3: spacer ─────────
  ws.addRow([]).height = 6;

  // ───────── ROW 4: Course/Type sub-headers ─────────
  const subHeaderLabels = ['', '', ''];
  assessments.forEach(a => {
    subHeaderLabels.push(a.course_title || '');
  });
  subHeaderLabels.push('');
  const subRow = ws.addRow(subHeaderLabels);
  subRow.height = 16;
  subRow.eachCell((cell, colNumber) => {
    cell.font = { size: 8, color: { argb: C.gray500 }, name: 'Calibri', italic: true };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.gray50 } };
    cell.alignment = { vertical: 'middle', horizontal: colNumber <= 3 ? 'left' : 'center' };
    cell.border = { top: noBorder, bottom: noBorder, left: noBorder, right: noBorder };
  });

  // ───────── ROW 5: Table headers ─────────
  const headerLabels = ['#', 'Candidat', 'Email'];
  assessments.forEach(a => {
    const typeLabel = typeLabels[a.type] || a.type;
    headerLabels.push(`${a.title}\n(${typeLabel})`);
  });
  headerLabels.push('Moyenne\n/20');
  const headerRow = ws.addRow(headerLabels);
  headerRow.height = 34;
  headerRow.eachCell((cell, colNumber) => {
    cell.font = { bold: true, size: 9, color: { argb: C.white }, name: 'Calibri' };
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purple } };
    cell.alignment = { vertical: 'middle', horizontal: colNumber <= 3 ? 'left' : 'center', wrapText: true, indent: colNumber === 1 ? 1 : 0 };
    cell.border = {
      top: thinBorder(C.purple), bottom: thinBorder(C.purple),
      left: thinBorder(C.purple), right: thinBorder(C.purple),
    };
  });
  // Last col (Moyenne) special: gold bg
  const moyenneHeaderCell = headerRow.getCell(totalCols);
  moyenneHeaderCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD97706' } };

  // ───────── DATA ROWS ─────────
  students.forEach((student, i) => {
    const avg = filterCourse !== 'all' ? student.filteredAverage : student.average;
    const rank = filterCourse !== 'all' ? (avg !== null ? i + 1 : null) : student.rank;

    const rowData = [
      rank ?? '-',
      student.name || '-',
      student.email || '-',
    ];
    assessments.forEach(a => {
      const g = student.grades[a.id];
      rowData.push(g ? g.normalised.toFixed(1) : '—');
    });
    rowData.push(avg !== null && avg !== undefined ? avg.toFixed(1) : '—');

    const dataRow = ws.addRow(rowData);
    dataRow.height = 24;

    const isEven = i % 2 === 0;
    const bgColor = isEven ? C.gray50 : C.white;

    dataRow.eachCell((cell, colNumber) => {
      cell.font = { size: 10, color: { argb: C.gray800 }, name: 'Calibri' };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } };
      cell.alignment = { vertical: 'middle', horizontal: colNumber <= 3 ? 'left' : 'center', indent: colNumber === 1 ? 1 : 0 };
      cell.border = { top: noBorder, bottom: thinBorder(C.gray200), left: noBorder, right: noBorder };
    });

    // Color-code each assessment note
    assessments.forEach((a, ai) => {
      const g = student.grades[a.id];
      if (g) {
        const cell = dataRow.getCell(4 + ai); // offset by 3 fixed cols
        cell.font = { bold: true, size: 10, color: { argb: noteColor(g.normalised) }, name: 'Calibri' };
      }
    });

    // Color-code moyenne
    if (avg !== null && avg !== undefined) {
      const avgCell = dataRow.getCell(totalCols);
      avgCell.font = { bold: true, size: 11, color: { argb: C.white }, name: 'Calibri' };
      avgCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: noteColor(avg) } };
      avgCell.alignment = { vertical: 'middle', horizontal: 'center' };
    }

    // Rank medal styling
    if (rank && rank <= 3) {
      const rankCell = dataRow.getCell(1);
      const colors = { 1: 'FFF59E0B', 2: 'FF94A3B8', 3: 'FFEA580C' };
      rankCell.font = { bold: true, size: 10, color: { argb: colors[rank] || C.gray500 }, name: 'Calibri' };
    }
  });

  // ───────── SPACER ─────────
  ws.addRow([]).height = 10;
  ws.addRow([]).height = 6;

  // ───────── STATISTICS SECTION ─────────
  const statsStartRow = ws.rowCount + 1;
  const statsRow1 = ws.addRow([]);
  ws.mergeCells(statsStartRow, 1, statsStartRow, totalCols);
  const stCell = ws.getCell(statsStartRow, 1);
  stCell.value = '📊  Récapitulatif & Statistiques';
  stCell.font = { bold: true, size: 13, color: { argb: C.purple }, name: 'Calibri' };
  stCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
  stCell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
  stCell.border = {
    top: thinBorder(C.purple), bottom: noBorder,
    left: { style: 'medium', color: { argb: C.purple } },
    right: thinBorder(C.purple),
  };
  statsRow1.height = 32;
  for (let c = 2; c <= totalCols; c++) {
    const cell = ws.getCell(statsStartRow, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
    cell.border = {
      top: thinBorder(C.purple), bottom: noBorder, left: noBorder,
      right: c === totalCols ? thinBorder(C.purple) : noBorder,
    };
  }

  const statItems1 = [
    { label: 'Candidats', value: String(stats.totalStudents), color: C.gray800 },
    { label: 'Épreuves', value: String(stats.totalAssessments), color: C.purple },
    { label: 'Moyenne générale', value: `${stats.avg}/20`, color: C.gray800 },
    { label: 'Taux de réussite (≥10)', value: `${stats.successRate}%`, color: C.purple },
  ];
  const statItems2 = [
    { label: 'Meilleure moyenne', value: `${stats.best}/20`, color: C.success },
    { label: 'Plus basse moyenne', value: `${stats.worst}/20`, color: C.danger },
    { label: 'Au-dessus de 10', value: String(stats.above10), color: C.success },
    { label: 'En dessous de 10', value: String(stats.below10), color: C.danger },
  ];

  // Calculate stat column positions dynamically
  const statColSpan = Math.max(2, Math.floor(totalCols / 4));
  const statPositions = [];
  for (let i = 0; i < 4; i++) {
    statPositions.push(1 + i * statColSpan);
  }

  const writeStatBlock = (items, isLast) => {
    // Value row
    const valRowNum = ws.rowCount + 1;
    const valRow = ws.addRow([]);
    valRow.height = 28;

    items.forEach((item, idx) => {
      const col = statPositions[idx] || (1 + idx * 2);
      const endCol = Math.min(col + statColSpan - 1, totalCols);
      if (endCol > col) ws.mergeCells(valRowNum, col, valRowNum, endCol);
      const cell = ws.getCell(valRowNum, col);
      cell.value = item.value;
      cell.font = { bold: true, size: 16, color: { argb: item.color }, name: 'Calibri' };
      cell.alignment = { vertical: 'bottom', horizontal: 'left', indent: 1 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
    });
    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getCell(valRowNum, c);
      if (!cell.value && cell.value !== 0) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
      cell.border = {
        top: noBorder, bottom: noBorder,
        left: c === 1 ? { style: 'medium', color: { argb: C.purple } } : noBorder,
        right: c === totalCols ? thinBorder(C.purple) : noBorder,
      };
    }

    // Label row
    const lblRowNum = ws.rowCount + 1;
    const lblRow = ws.addRow([]);
    lblRow.height = 16;
    items.forEach((item, idx) => {
      const col = statPositions[idx] || (1 + idx * 2);
      const endCol = Math.min(col + statColSpan - 1, totalCols);
      if (endCol > col) ws.mergeCells(lblRowNum, col, lblRowNum, endCol);
      const cell = ws.getCell(lblRowNum, col);
      cell.value = item.label;
      cell.font = { size: 8, color: { argb: C.gray500 }, name: 'Calibri' };
      cell.alignment = { vertical: 'top', horizontal: 'left', indent: 1 };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
    });
    for (let c = 1; c <= totalCols; c++) {
      const cell = ws.getCell(lblRowNum, c);
      if (!cell.value) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
      cell.border = {
        top: noBorder,
        bottom: isLast ? thinBorder(C.purple) : noBorder,
        left: c === 1 ? { style: 'medium', color: { argb: C.purple } } : noBorder,
        right: c === totalCols ? thinBorder(C.purple) : noBorder,
      };
    }
  };

  writeStatBlock(statItems1, false);
  // Spacer
  const spR = ws.addRow([]);
  spR.height = 4;
  for (let c = 1; c <= totalCols; c++) {
    const cell = ws.getCell(ws.rowCount, c);
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: C.purpleBg } };
    cell.border = {
      top: noBorder, bottom: noBorder,
      left: c === 1 ? { style: 'medium', color: { argb: C.purple } } : noBorder,
      right: c === totalCols ? thinBorder(C.purple) : noBorder,
    };
  }
  writeStatBlock(statItems2, true);

  // ── Footer ──
  ws.addRow([]).height = 8;
  const footerRowNum = ws.rowCount + 1;
  ws.addRow([]);
  ws.mergeCells(footerRowNum, 1, footerRowNum, totalCols);
  const fCell = ws.getCell(footerRowNum, 1);
  fCell.value = `DevOpsLearn — ${workspaceTitle}  •  Généré le ${dateStr}`;
  fCell.font = { size: 8, color: { argb: C.gray400 }, name: 'Calibri', italic: true };
  fCell.alignment = { vertical: 'middle', horizontal: 'center' };
  fCell.border = { top: thinBorder(C.gray200), bottom: noBorder, left: noBorder, right: noBorder };

  // ── Save ──
  const fileName = `recap_notes_${workspaceTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.xlsx`;
  const buffer = await wb.xlsx.writeBuffer();
  saveAs(new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), fileName);
}

// ─────────────────────────────────────────────
//  PDF EXPORT
// ─────────────────────────────────────────────
export function exportRecapToPDF(students, assessments, workspaceTitle, filterCourse) {
  const stats = buildRecapStats(students, assessments, filterCourse);

  const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();

  const PRIMARY = [109, 40, 217];
  const SUCCESS = [5, 150, 105];
  const DANGER = [239, 68, 68];
  const GRAY_800 = [30, 41, 59];
  const GRAY_500 = [100, 116, 139];
  const GRAY_200 = [226, 232, 240];
  const WHITE = [255, 255, 255];

  // ── Header banner ──
  doc.setFillColor(...PRIMARY);
  doc.rect(0, 0, pageW, 28, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(18);
  doc.setTextColor(...WHITE);
  doc.text('Récapitulatif des notes', 14, 12);

  doc.setFontSize(12);
  doc.setFont('helvetica', 'normal');
  doc.text(workspaceTitle, 14, 20);

  doc.setFontSize(9);
  const exportDate = new Date().toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  doc.text(`Exporté le ${exportDate}`, pageW - 14, 12, { align: 'right' });
  doc.text(`${stats.totalStudents} candidat(s) · ${stats.totalAssessments} épreuve(s)`, pageW - 14, 19, { align: 'right' });

  // ── Table layout ──
  // Fixed cols: # (8), Candidat (42), then dynamic assessment cols, then Moyenne (18)
  const fixedLeft = 8 + 42;
  const fixedRight = 18;
  const margins = 14;
  const availableW = pageW - 2 * margins;
  const dynamicW = availableW - fixedLeft - fixedRight;
  const assessColW = assessments.length > 0 ? Math.min(22, Math.max(14, dynamicW / assessments.length)) : 18;
  const totalTableW = fixedLeft + assessments.length * assessColW + fixedRight;
  const tX = Math.max(margins, (pageW - totalTableW) / 2);

  const cols = [
    { label: '#', width: 8, align: 'center', key: 'rank' },
    { label: 'Candidat', width: 42, align: 'left', key: 'name' },
  ];
  assessments.forEach(a => {
    const typeLabel = typeLabels[a.type] || a.type;
    cols.push({ label: a.title, subLabel: typeLabel, width: assessColW, align: 'center', key: a.id });
  });
  cols.push({ label: 'Moyenne', width: fixedRight, align: 'center', key: 'moyenne' });

  const rowHeight = 7;
  const headerHeight = 12;
  let curY = 34;

  const drawTableHeader = () => {
    // Header bg
    doc.setFillColor(...PRIMARY);
    doc.rect(tX, curY, totalTableW, headerHeight, 'F');

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(6.5);
    doc.setTextColor(...WHITE);

    let cx = tX;
    cols.forEach(col => {
      const textX = col.align === 'center' ? cx + col.width / 2 : cx + 2;
      if (col.subLabel) {
        doc.setFontSize(5.5);
        doc.text(col.label.length > 12 ? col.label.substring(0, 11) + '…' : col.label, textX, curY + 5, { align: col.align === 'center' ? 'center' : 'left' });
        doc.setFontSize(4.5);
        doc.setFont('helvetica', 'normal');
        doc.text(col.subLabel, textX, curY + 9, { align: 'center' });
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(6.5);
      } else {
        doc.text(col.label, textX, curY + 7.5, { align: col.align === 'center' ? 'center' : 'left' });
      }
      cx += col.width;
    });

    // Moyenne header with gold bg
    const lastCol = cols[cols.length - 1];
    const lastX = tX + totalTableW - lastCol.width;
    doc.setFillColor(217, 119, 6);
    doc.rect(lastX, curY, lastCol.width, headerHeight, 'F');
    doc.setTextColor(...WHITE);
    doc.setFontSize(6.5);
    doc.text('Moyenne', lastX + lastCol.width / 2, curY + 7.5, { align: 'center' });

    curY += headerHeight;
  };

  drawTableHeader();

  // ── Data rows ──
  doc.setFontSize(6.5);

  students.forEach((student, i) => {
    const avg = filterCourse !== 'all' ? student.filteredAverage : student.average;
    const rank = filterCourse !== 'all' ? (avg !== null ? i + 1 : null) : student.rank;

    // Page overflow
    if (curY + rowHeight > pageH - 48) {
      doc.addPage();
      curY = 14;
      drawTableHeader();
      doc.setFontSize(6.5);
    }

    // Alternating bg
    if (i % 2 === 0) {
      doc.setFillColor(248, 250, 252);
      doc.rect(tX, curY, totalTableW, rowHeight, 'F');
    }

    // Bottom line
    doc.setDrawColor(...GRAY_200);
    doc.setLineWidth(0.15);
    doc.line(tX, curY + rowHeight, tX + totalTableW, curY + rowHeight);

    let rx = tX;
    cols.forEach(col => {
      const textX = col.align === 'center' ? rx + col.width / 2 : rx + 2;

      if (col.key === 'rank') {
        doc.setFont('helvetica', 'bold');
        doc.setTextColor(...GRAY_500);
        if (rank && rank <= 3) {
          const colors = { 1: [245, 158, 11], 2: [148, 163, 184], 3: [234, 88, 12] };
          doc.setTextColor(...(colors[rank] || GRAY_500));
        }
        doc.text(rank ? String(rank) : '-', textX, curY + 5, { align: 'center' });
      } else if (col.key === 'name') {
        doc.setFont('helvetica', 'normal');
        doc.setTextColor(...GRAY_800);
        let name = student.name || '-';
        if (name.length > 24) name = name.substring(0, 22) + '…';
        doc.text(name, textX, curY + 5);
      } else if (col.key === 'moyenne') {
        if (avg !== null && avg !== undefined) {
          // Draw colored pill
          const pillW = col.width - 3;
          const pillH = 5;
          const pillX = rx + 1.5;
          const pillY = curY + 1;
          const nc = noteColorRGB(avg);
          doc.setFillColor(...nc);
          doc.roundedRect(pillX, pillY, pillW, pillH, 1.5, 1.5, 'F');
          doc.setFont('helvetica', 'bold');
          doc.setFontSize(6.5);
          doc.setTextColor(...WHITE);
          doc.text(avg.toFixed(1), pillX + pillW / 2, curY + 4.8, { align: 'center' });
        } else {
          doc.setTextColor(...GRAY_200);
          doc.text('—', textX, curY + 5, { align: 'center' });
        }
      } else {
        // Assessment grade
        const g = student.grades[col.key];
        if (g) {
          doc.setFont('helvetica', 'bold');
          doc.setTextColor(...noteColorRGB(g.normalised));
          doc.text(g.normalised.toFixed(1), textX, curY + 5, { align: 'center' });
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(...GRAY_200);
          doc.text('—', textX, curY + 5, { align: 'center' });
        }
      }
      rx += col.width;
    });

    doc.setFontSize(6.5);
    curY += rowHeight;
  });

  // ── Statistics section ──
  curY += 6;
  if (curY + 42 > pageH - 12) {
    doc.addPage();
    curY = 14;
  }

  const statsW = totalTableW;
  const statsH = 38;
  const statsX = tX;

  doc.setFillColor(248, 250, 252);
  doc.roundedRect(statsX, curY, statsW, statsH, 3, 3, 'F');
  doc.setDrawColor(...GRAY_200);
  doc.setLineWidth(0.3);
  doc.roundedRect(statsX, curY, statsW, statsH, 3, 3, 'S');
  doc.setFillColor(...PRIMARY);
  doc.rect(statsX, curY, 4, statsH, 'F');

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.setTextColor(...PRIMARY);
  doc.text('Récapitulatif & Statistiques', statsX + 12, curY + 8);

  const statItems = [
    { label: 'Candidats', value: String(stats.totalStudents), color: GRAY_800 },
    { label: 'Épreuves', value: String(stats.totalAssessments), color: PRIMARY },
    { label: 'Moyenne générale', value: `${stats.avg}/20`, color: GRAY_800 },
    { label: 'Taux de réussite', value: `${stats.successRate}%`, color: PRIMARY },
    { label: 'Meilleure moyenne', value: `${stats.best}/20`, color: SUCCESS },
    { label: 'Plus basse moyenne', value: `${stats.worst}/20`, color: DANGER },
    { label: 'Au-dessus de 10', value: String(stats.above10), color: SUCCESS },
    { label: 'En dessous de 10', value: String(stats.below10), color: DANGER },
  ];

  const statColW = (statsW - 20) / 4;
  const statStartX = statsX + 12;

  statItems.forEach((item, i) => {
    const col = i % 4;
    const row = Math.floor(i / 4);
    const sx = statStartX + col * statColW;
    const sy = curY + 14 + row * 12;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(...item.color);
    doc.text(item.value, sx, sy);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(6);
    doc.setTextColor(...GRAY_500);
    doc.text(item.label, sx, sy + 4);
  });

  // ── Footer ──
  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    doc.setTextColor(...GRAY_500);
    doc.text(`DevOpsLearn — ${workspaceTitle}`, 14, pageH - 6);
    doc.text(`Page ${p}/${totalPages}`, pageW - 14, pageH - 6, { align: 'right' });
    doc.setDrawColor(...GRAY_200);
    doc.setLineWidth(0.3);
    doc.line(14, pageH - 10, pageW - 14, pageH - 10);
  }

  const fileName = `recap_notes_${workspaceTitle.replace(/[^a-zA-Z0-9]/g, '_')}_${new Date().toISOString().slice(0, 10)}.pdf`;
  doc.save(fileName);
}
